import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { PlusIcon, EditIcon, SaveIcon, XIcon, CheckIcon, BarChart2Icon, AlertTriangleIcon } from 'lucide-react';
export function VolumePlans({
  locale,
  onAddPlan,
  onEditPlan,
  onViewPlan,
  showPlanForm,
  setShowPlanForm,
  selectedPlan
}) {
  // Mock data for volume plans
  const [availablePlans, setAvailablePlans] = useState([{
    id: 'plan-1',
    name: 'Enterprise Document Plan',
    maxValue: 100000,
    metric: 'Documents',
    billingPeriod: 'Annual',
    cost: 15000,
    currency: 'USD',
    description: 'Enterprise-level document processing plan with OCR capabilities'
  }, {
    id: 'plan-2',
    name: 'Business Invoice Plan',
    maxValue: 50000,
    metric: 'Invoices',
    billingPeriod: 'Annual',
    cost: 10000,
    currency: 'USD',
    description: 'Mid-sized business invoice processing plan'
  }, {
    id: 'plan-3',
    name: 'Starter Vendor Plan',
    maxValue: 1000,
    metric: 'Vendors',
    billingPeriod: 'Monthly',
    cost: 500,
    currency: 'USD',
    description: 'Entry-level vendor management plan for small businesses'
  }, {
    id: 'plan-4',
    name: 'Advanced Analytics Plan',
    maxValue: 250000,
    metric: 'Data Points',
    billingPeriod: 'Annual',
    cost: 20000,
    currency: 'USD',
    description: 'Advanced analytics and reporting plan with custom dashboards'
  }]);
  // Mock data for tenant volume plans (assigned plans)
  const [tenantPlans, setTenantPlans] = useState([{
    id: 'tenant-plan-1',
    planId: 'plan-1',
    planName: 'Enterprise Document Plan',
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    currentUsage: 75420,
    maxValue: 100000,
    metric: 'Documents',
    autoRenew: true,
    entities: ['Global Technologies Inc.'],
    sites: ['All Sites'],
    status: 'Active'
  }, {
    id: 'tenant-plan-2',
    planId: 'plan-2',
    planName: 'Business Invoice Plan',
    startDate: '2023-03-15',
    endDate: '2024-03-14',
    currentUsage: 22456,
    maxValue: 50000,
    metric: 'Invoices',
    autoRenew: false,
    entities: ['European Operations Ltd'],
    sites: ['All Sites'],
    status: 'Active'
  }]);
  // Form state for adding/editing plans
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    maxValue: 0,
    metric: '',
    billingPeriod: 'Annual',
    cost: 0,
    currency: 'USD',
    description: ''
  });
  // Form state for assigning plans
  const [assignFormData, setAssignFormData] = useState({
    planId: '',
    startDate: '',
    endDate: '',
    autoRenew: true,
    entities: [],
    sites: []
  });
  const [showAssignForm, setShowAssignForm] = useState(false);
  // Initialize form when editing
  useEffect(() => {
    if (selectedPlan && showPlanForm) {
      setFormData({
        ...selectedPlan
      });
    } else if (showPlanForm) {
      // Reset form for new plan
      setFormData({
        id: '',
        name: '',
        maxValue: 10000,
        metric: '',
        billingPeriod: 'Annual',
        cost: 0,
        currency: 'USD',
        description: ''
      });
    }
  }, [selectedPlan, showPlanForm]);
  // Handle form field changes
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  // Handle assign form field changes
  const handleAssignChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setAssignFormData({
      ...assignFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    if (formData.id) {
      // Update existing plan
      setAvailablePlans(availablePlans.map(plan => plan.id === formData.id ? {
        ...formData
      } : plan));
    } else {
      // Add new plan
      const newPlan = {
        ...formData,
        id: `plan-${new Date().getTime()}`
      };
      setAvailablePlans([...availablePlans, newPlan]);
    }
    setShowPlanForm(false);
  };
  // Handle plan assignment
  const handleAssignPlan = e => {
    e.preventDefault();
    const selectedPlan = availablePlans.find(plan => plan.id === assignFormData.planId);
    if (selectedPlan) {
      const newTenantPlan = {
        id: `tenant-plan-${new Date().getTime()}`,
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        startDate: assignFormData.startDate,
        endDate: assignFormData.endDate,
        currentUsage: 0,
        maxValue: selectedPlan.maxValue,
        metric: selectedPlan.metric,
        autoRenew: assignFormData.autoRenew,
        entities: assignFormData.entities,
        sites: assignFormData.sites,
        status: 'Active'
      };
      setTenantPlans([...tenantPlans, newTenantPlan]);
    }
    setShowAssignForm(false);
  };
  // Handle toggle auto-renew
  const handleToggleAutoRenew = tenantPlanId => {
    setTenantPlans(tenantPlans.map(plan => {
      if (plan.id === tenantPlanId) {
        return {
          ...plan,
          autoRenew: !plan.autoRenew
        };
      }
      return plan;
    }));
  };
  // Show assign plan form
  const handleShowAssignForm = () => {
    setAssignFormData({
      planId: availablePlans.length > 0 ? availablePlans[0].id : '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      autoRenew: true,
      entities: ['Global Technologies Inc.'],
      sites: ['All Sites']
    });
    setShowAssignForm(true);
  };
  return <div className="space-y-8" data-id="element-18335">
      {/* Available Plans Section */}
      {!showPlanForm && !showAssignForm && <>
          <div data-id="element-18336">
            <div className="flex justify-between items-center mb-4" data-id="element-18337">
              <div data-id="element-18338">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-18339">
                  Available Volume Plans
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-18340">
                  Manage your volume-based licensing plans
                </p>
              </div>
              <div className="flex space-x-3" data-id="element-18341">
                <button type="button" onClick={handleShowAssignForm} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18342">
                  <CheckIcon className="h-4 w-4 mr-2" data-id="element-18343" />
                  Assign Plan
                </button>
                <button type="button" onClick={onAddPlan} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18344">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-18345" />
                  Add Plan
                </button>
              </div>
            </div>
            <div className="flex flex-col" data-id="element-18346">
              <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-18347">
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-18348">
                  <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-18349">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-18350">
                      <thead className="bg-gray-50" data-id="element-18351">
                        <tr data-id="element-18352">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18353">
                            Plan Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18354">
                            Metric
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18355">
                            Max Value
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18356">
                            Billing Period
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18357">
                            Cost
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18358">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-18359">
                        {availablePlans.map(plan => <tr key={plan.id} data-id="element-18360">
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-18361">
                              <div className="flex items-center" data-id="element-18362">
                                <div className="ml-4" data-id="element-18363">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-18364">
                                    {plan.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1" data-id="element-18365">
                                    {plan.description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-18366">
                              <div className="text-sm text-gray-900" data-id="element-18367">
                                {plan.metric}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-18368">
                              <div className="text-sm text-gray-900" data-id="element-18369">
                                {plan.maxValue.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-18370">
                              <div className="text-sm text-gray-900" data-id="element-18371">
                                {plan.billingPeriod}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-18372">
                              <div className="text-sm text-gray-900" data-id="element-18373">
                                {formatCurrency(plan.cost, plan.currency, locale)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-18374">
                              <div className="flex justify-end space-x-2" data-id="element-18375">
                                <button onClick={() => onEditPlan(plan)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-18376">
                                  <EditIcon className="h-4 w-4" data-id="element-18377" />
                                </button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                    {availablePlans.length === 0 && <div className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-18378">
                        No volume plans found. Click "Add Plan" to create one.
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Tenant Volume Plans Section */}
          <div data-id="element-18379">
            <div className="flex justify-between items-center mb-4" data-id="element-18380">
              <div data-id="element-18381">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-18382">
                  Assigned Volume Plans
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-18383">
                  Currently assigned volume plans and their usage
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-18384">
              {tenantPlans.map(plan => {
            // Calculate usage percentage
            const usagePercentage = Math.round(plan.currentUsage / plan.maxValue * 100);
            const isNearLimit = usagePercentage >= 75;
            return <div key={plan.id} className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200" data-id="element-18385">
                    <div className="px-4 py-5 sm:px-6" data-id="element-18386">
                      <div className="flex justify-between" data-id="element-18387">
                        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18388">
                          {plan.planName}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${plan.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-18389">
                          {plan.status}
                        </span>
                      </div>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-18390">
                        {plan.metric} Plan • {plan.entities.join(', ')}
                      </p>
                    </div>
                    <div className="px-4 py-4 sm:px-6" data-id="element-18391">
                      <div className="flex justify-between mb-2" data-id="element-18392">
                        <span className="text-sm font-medium text-gray-500" data-id="element-18393">
                          Usage
                        </span>
                        <span className="text-sm font-medium text-gray-900" data-id="element-18394">
                          {plan.currentUsage.toLocaleString()} /{' '}
                          {plan.maxValue.toLocaleString()} {plan.metric}
                        </span>
                      </div>
                      <div className="relative" data-id="element-18395">
                        <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-100" data-id="element-18396">
                          <div style={{
                      width: `${usagePercentage}%`
                    }} className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${isNearLimit ? 'bg-yellow-500' : 'bg-indigo-500'}`} data-id="element-18397"></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1 text-right" data-id="element-18398">
                          {usagePercentage}% used
                        </div>
                      </div>
                      {isNearLimit && <div className="mt-3 flex items-center text-sm text-yellow-600" data-id="element-18399">
                          <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-18400" />
                          Approaching usage limit
                        </div>}
                      <div className="mt-4 grid grid-cols-2 gap-4" data-id="element-18401">
                        <div data-id="element-18402">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-18403">
                            Start Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-18404">
                            {new Date(plan.startDate).toLocaleDateString(locale)}
                          </dd>
                        </div>
                        <div data-id="element-18405">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-18406">
                            End Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-18407">
                            {new Date(plan.endDate).toLocaleDateString(locale)}
                          </dd>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between" data-id="element-18408">
                        <div className="flex items-center" data-id="element-18409">
                          <input id={`auto-renew-${plan.id}`} name="auto-renew" type="checkbox" checked={plan.autoRenew} onChange={() => handleToggleAutoRenew(plan.id)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18410" />
                          <label htmlFor={`auto-renew-${plan.id}`} className="ml-2 block text-sm text-gray-900" data-id="element-18411">
                            Auto-renew
                          </label>
                        </div>
                        <button type="button" onClick={() => onViewPlan(plan)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18412">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>;
          })}
              {tenantPlans.length === 0 && <div className="col-span-full bg-white overflow-hidden shadow rounded-lg" data-id="element-18413">
                  <div className="px-4 py-5 sm:p-6 text-center" data-id="element-18414">
                    <BarChart2Icon className="mx-auto h-12 w-12 text-gray-400" data-id="element-18415" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-18416">
                      No assigned plans
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-18417">
                      Get started by assigning a volume plan to your
                      organization.
                    </p>
                    <div className="mt-6" data-id="element-18418">
                      <button type="button" onClick={handleShowAssignForm} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18419">
                        <CheckIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" data-id="element-18420" />
                        Assign Plan
                      </button>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </>}
      {/* Volume Plan Form */}
      {showPlanForm && <div className="bg-white shadow sm:rounded-lg" data-id="element-18421">
          <div className="px-4 py-5 sm:p-6" data-id="element-18422">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18423">
              {formData.id ? 'Edit Volume Plan' : 'Add Volume Plan'}
            </h3>
            <form className="mt-5 space-y-6" onSubmit={handleSubmit} data-id="element-18424">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-18425">
                <div className="sm:col-span-3" data-id="element-18426">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-18427">
                    Plan Name
                  </label>
                  <div className="mt-1" data-id="element-18428">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18429" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18430">
                  <label htmlFor="metric" className="block text-sm font-medium text-gray-700" data-id="element-18431">
                    Metric
                  </label>
                  <div className="mt-1" data-id="element-18432">
                    <input type="text" name="metric" id="metric" value={formData.metric} onChange={handleChange} placeholder="e.g. Documents, Invoices, Pages" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18433" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18434">
                  <label htmlFor="maxValue" className="block text-sm font-medium text-gray-700" data-id="element-18435">
                    Max Value
                  </label>
                  <div className="mt-1" data-id="element-18436">
                    <input type="number" name="maxValue" id="maxValue" value={formData.maxValue} onChange={handleChange} min="1" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18437" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18438">
                  <label htmlFor="billingPeriod" className="block text-sm font-medium text-gray-700" data-id="element-18439">
                    Billing Period
                  </label>
                  <div className="mt-1" data-id="element-18440">
                    <select id="billingPeriod" name="billingPeriod" value={formData.billingPeriod} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18441">
                      <option value="Monthly" data-id="element-18442">Monthly</option>
                      <option value="Quarterly" data-id="element-18443">Quarterly</option>
                      <option value="Annual" data-id="element-18444">Annual</option>
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18445">
                  <label htmlFor="cost" className="block text-sm font-medium text-gray-700" data-id="element-18446">
                    Cost
                  </label>
                  <div className="mt-1" data-id="element-18447">
                    <input type="number" name="cost" id="cost" value={formData.cost} onChange={handleChange} min="0" step="0.01" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18448" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18449">
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-18450">
                    Currency
                  </label>
                  <div className="mt-1" data-id="element-18451">
                    <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18452">
                      <option value="USD" data-id="element-18453">USD</option>
                      <option value="EUR" data-id="element-18454">EUR</option>
                      <option value="GBP" data-id="element-18455">GBP</option>
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-18456">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-18457">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-18458">
                    <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18459" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-18460">
                <button type="button" onClick={() => setShowPlanForm(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18461">
                  Cancel
                </button>
                <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18462">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-18463" />
                  {formData.id ? 'Update Plan' : 'Add Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Assign Plan Form */}
      {showAssignForm && <div className="bg-white shadow sm:rounded-lg" data-id="element-18464">
          <div className="px-4 py-5 sm:p-6" data-id="element-18465">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18466">
              Assign Volume Plan
            </h3>
            <form className="mt-5 space-y-6" onSubmit={handleAssignPlan} data-id="element-18467">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-18468">
                <div className="sm:col-span-6" data-id="element-18469">
                  <label htmlFor="planId" className="block text-sm font-medium text-gray-700" data-id="element-18470">
                    Select Plan
                  </label>
                  <div className="mt-1" data-id="element-18471">
                    <select id="planId" name="planId" value={assignFormData.planId} onChange={handleAssignChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18472">
                      <option value="" data-id="element-18473">Select a plan</option>
                      {availablePlans.map(plan => <option key={plan.id} value={plan.id} data-id="element-18474">
                          {plan.name} - {plan.maxValue.toLocaleString()}{' '}
                          {plan.metric} -{' '}
                          {formatCurrency(plan.cost, plan.currency, locale)}
                        </option>)}
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18475">
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-18476">
                    Start Date
                  </label>
                  <div className="mt-1" data-id="element-18477">
                    <input type="date" name="startDate" id="startDate" value={assignFormData.startDate} onChange={handleAssignChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18478" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-18479">
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-18480">
                    End Date
                  </label>
                  <div className="mt-1" data-id="element-18481">
                    <input type="date" name="endDate" id="endDate" value={assignFormData.endDate} onChange={handleAssignChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-18482" />
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-18483">
                  <label htmlFor="entities" className="block text-sm font-medium text-gray-700" data-id="element-18484">
                    Assign to Entities
                  </label>
                  <div className="mt-1" data-id="element-18485">
                    <select id="entities" name="entities" value={assignFormData.entities} onChange={handleAssignChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" multiple data-id="element-18486">
                      <option value="Global Technologies Inc." data-id="element-18487">
                        Global Technologies Inc.
                      </option>
                      <option value="European Operations Ltd" data-id="element-18488">
                        European Operations Ltd
                      </option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-18489">
                      Hold Ctrl/Cmd to select multiple entities
                    </p>
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-18490">
                  <div className="flex items-center" data-id="element-18491">
                    <input id="autoRenew" name="autoRenew" type="checkbox" checked={assignFormData.autoRenew} onChange={handleAssignChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18492" />
                    <label htmlFor="autoRenew" className="ml-2 block text-sm text-gray-900" data-id="element-18493">
                      Enable auto-renewal
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-18494">
                <button type="button" onClick={() => setShowAssignForm(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18495">
                  Cancel
                </button>
                <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18496">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-18497" />
                  Assign Plan
                </button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
}